import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Game from './components/game';
import { setup } from './state/actions/setup';
import { logout } from './state/actions/auth';
import { getUser } from './state/actions/user';
import { setAppView } from './state/actions/app-view';
import Preloader from './components/shared/preloader';
import mainAudioSrc from '../assets/sounds/boat-harbor-ambience.mp3';
import { setStep, storeStepsData } from './state/actions/step';
import { createNewResultsFactory, findActiveStepIndex, redirectToNeon } from './helpers';
import { createResults, getResults } from './state/actions/results';
import autobind from 'autobind-decorator';
import ResultState from "./constants/ResultState";
import UserState from "./constants/UserState";
import SetupState from "./constants/SetupState";
import Logger from "./tools/logger";

/**
 * @class App
 *
 * @description
 * Class for the main app.
 *
 * @example
 * <App />
 */
@connect(
	state => ({
		appView: state.appView,
		sectionState: state.setup.sectionState,
		testSuites: state.setup.testSuites,
		steps: state.step.steps,
		resultsData: state.results,
		setupData: state.setup,
		userData: state.user
	}),
	dispatch => {
		return ({
			setActiveStep: (activeIndex) => {
				dispatch(setStep(activeIndex));
			},
			setup: () => {
				dispatch(setup());
			},
			setAppView: (view) => {
				dispatch(setAppView(view));
			},
			storeStepsData: (steps) => {
				dispatch(storeStepsData(steps));
			},
			createResults: async (body) => {
				dispatch(createResults(body));
			},
			getResults: async () => {
				dispatch(getResults());
			},
			logout: async () => {
				dispatch(logout());
			},
			getUserData: async () => {
				dispatch(getUser());
			}
		})
	}
)
export default class App extends Component {

	static PropTypes = {
		setup: PropTypes.func,
		getData: PropTypes.func,
		setAppView: PropTypes.func,
		createResults: PropTypes.func,
		getResults: PropTypes.func,
		appView: PropTypes.string,
		setupData: PropTypes.object
	};

	constructor(props) {
		super(props);

		this.state = {
			loggedIn: false,
			creatingNewResults: false,
			fetchingUser: false,
			fetchingResults: false,
			width: 0,
			height: 0
		};

		// init logger
		const logger = new Logger({
			devModeBaseUrl: 'https://api.staging.ltponline.com',
			devMode: false,
			baseUrl: `${process.env.NEON_API_BASE_URL}`,
			fetchTokenUrl: `${process.env.NEON_API_BASE_URL}/api/v1/logzio/key`
		});

		logger.notice({
			component: 'application',
			message: 'Open application'
		});
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.updateWindowDimensions);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		const {resultsData, userData, setupData} = this.props;

		// detect a change in result fetching and setup data fetching
		// as we can only create a new result set when the resultsData is empty
		// and when the setupData is loaded. Both are happening async
		if (prevProps.resultsData.resultsState !== resultsData.resultsState
			|| prevProps.setupData.sectionState !== setupData.sectionState) {

			// when there are no existing results and setup data is loaded, post new result set
			if (resultsData.resultsState === ResultState.NO_RESULTS
				&& setupData.sectionState === SetupState.LOADED) {
				this.setState({fetchingResults: false}, () => {
                    this.createResults(this.props);
                });
			}
		}

		// redirect if required calls fail
		if (resultsData.resultsState === ResultState.ERROR_FETCHING
			|| resultsData.resultsState === ResultState.INIT_ERROR
			|| userData.userState === UserState.ERROR_FETCHING
			|| setupData.sectionState === SetupState.ERROR_FETCHING) {

		    // todo: add logging here?
			redirectToNeon();
		}

		// todo: implement later: act on save error state. Now happening in next-step-controller
		if (resultsData.resultsState === ResultState.SAVE_ERROR) {

		}

		this.getUsersData(this.props);
		this.stepsStorage(this.props);
		this.getResults(this.props);
		this.runGame(this.props);
	}

	async componentDidMount () {
		this.updateWindowDimensions();
		window.addEventListener('resize', this.updateWindowDimensions);

		this.props.setup();

		const mainAudio = new Audio(mainAudioSrc);
		await mainAudio.play();
	}

	@autobind
	updateWindowDimensions() {
		this.setState({ width: window.innerWidth, height: window.innerHeight });
	}

	stepsStorage ({resultsData, setupData, steps}) {
		if (!this.state.loggedIn) return;
		if (!resultsData || !setupData) return;
		if (!resultsData.testSuite || !setupData.testSuites) return;
		const activeTestSuite = resultsData.testSuite;
		const testSuitData = setupData.testSuites[activeTestSuite];
		if (!testSuitData.steps) return;
		if (steps) return;
		this.props.storeStepsData(testSuitData.steps);
		this.props.setActiveStep(findActiveStepIndex(resultsData, setupData));
	}

	runGame () {
		if (!this.state.loggedIn || !this.props.testSuites) return;
		if (!this.props.steps) return;
		if (!this.props.steps) return;
		if (!this.props.userData.profileData) return;
		if (this.props.appView === 'game') return;
		this.props.setAppView('game');
	}

	createResults ({resultsData, setupData}) {
		if (!this.state.loggedIn) return;
		if (this.state.creatingNewResults) {

		    // todo: add logging here
            return;
		}

		if (!resultsData || !setupData || setupData.sectionState === SetupState.LOADING) return;

		this.setState({creatingNewResults: true}, () => {
            this.props.createResults(createNewResultsFactory(setupData));
        });
	}

	getResults ({resultsData}) {
		if (!this.state.loggedIn) return;
		if (this.state.fetchingResults) {

		    // todo: add logging here?
		    return;
        }

		if (!resultsData) return;
		if (resultsData.saveState) {

		    // todo: add logging here?
		    return;
        }

		if (resultsData.resultsState !== ResultState.LOADING
			&& resultsData.resultsState !== ResultState.INIT_SUCCESS) return;

		this.setState({fetchingResults: true}, () => {
            this.props.getResults();
        });
	}

	getUsersData ({userData}) {
		if (this.state.fetchingUser) return;
		if (userData.userState === UserState.LOADING) return;

		this.setState({ fetchingUser: true }, () => {
            this.props.getUserData().then(() =>{
            	this.setState({ loggedIn: true });
			});
        });
	}

	getComponent (type) {
		let component = null;

		switch (type) {
			case 'loading':
				component = <Preloader message="Loading" />;
				break;
			case 'game':
				component = <Game />;
				break;
		}

		const screenTooSmall = this.getScreenTooSmall();

		if (screenTooSmall) {
			component = screenTooSmall;
		}

		return component;
	}

	getScreenTooSmall () {
		if (this.state.width !== 0 && this.state.width < 700) {
			return (
				<div className={'device-size-warning'}>
					<h1>Device too small, use a desktop</h1>
				</div>
			)
		}
		return false;
	}


	/**
	 * @description
	 * Component Method
	 * Renders a component
	 *
	 * @returns {ReactElement} component
	 */
	render () {
		return this.getComponent(this.props.appView)
	}
}
